import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, FieldArray } from "formik";
import {
    TextField,
    Typography,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Box,
    FormControl,
    Select,
    MenuItem,
    TableContainer,
} from "@mui/material";
import TimesheetEntryCard from "../../../../components/ui/TimesheetEntryCard";
import { UserApi } from "../../../../apis";
import { toast } from "react-toastify";
import {
    useParams,
    useSearchParams
} from "react-router-dom";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import WeekPicker from "../../../user/phases/weekPicker";
import dayjs from "dayjs";

const UserTimeSheet = () => {
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [username, setUsername] = useState("");
    const [statusMessage, setStatusMessage] = useState("Your time sheet status is pending.");
    const [statusColor, setStatusColor] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [projects, setProjects] = useState([]);
    const [timeSheetId, setTimeSheetId] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const { userId } = useParams();
    const formikRef = useRef(null);

    const initialValues = {
        rows: [
            {
                projectName: "",
                phaseName: "",
                hours: {},
                notes: "",
                description: "", // Added description field
            },
        ],
        notes: "",
        totalHours: 0,
    };

    const getStartOfWeek = (date, useLegacyFormat = false) => {
        const currentDate = dayjs(date);
        const cutoffDate = dayjs('2025-10-12'); // October 12, 2025
        
        // If legacy format or date is before October 12, 2025, use Sunday as start
        if (useLegacyFormat || currentDate.isBefore(cutoffDate, 'day')) {
            // Calculate Sunday of the week (legacy format)
            const dayOfWeek = currentDate.day(); // 0 = Sunday, 1 = Monday, etc.
            return currentDate.subtract(dayOfWeek, 'day').startOf('day'); // Go back to Sunday
        }
        
        // Use Monday as start (new format) - this uses the global Day.js config
        return currentDate.startOf("week");
    };

    const getWeekDays = (selectedDate) => {
        // Check if the selected week is before October 12, 2025
        const cutoffDate = dayjs('2025-10-09');
        const isOldData = dayjs(selectedDate).isBefore(cutoffDate, 'day');
        const startOfWeek = getStartOfWeek(selectedDate, isOldData);
        
        const weekDays = [];
        for (let i = 0; i < 7; i++) {
            const day = startOfWeek.add(i, "day");
            const dayFormatted = {
                day: day.format("ddd"),
                dateString: day.format("DD-MMM"),
            };
            weekDays.push(dayFormatted);
        }
        return weekDays;
    };

    const createEmptyRow = () => {
        const weekDays = getWeekDays(selectedDate);
        const hours = {};
        weekDays.forEach((day) => {
            hours[day.dateString] = 0;
        });
        return {
            projectName: "",
            phaseName: "",
            hours,
            notes: "",
            description: "", // Added description field
        };
    };

    const getWeekDates = (selectedDate) => {
        const cutoffDate = dayjs('2025-10-09');
        const isOldData = dayjs(selectedDate).isBefore(cutoffDate, 'day');
        const start = getStartOfWeek(selectedDate, isOldData);
        const end = start.add(6, "day");
        const startDate = start.format("DD-MM-YYYY");
        const endDate = end.format("DD-MM-YYYY");
        return { startDate, endDate };
    };

    const fetchProjects = async () => {
        setLoading(true);
        try {
            const response = await UserApi.getAssignedProjectsById(userId);
            const fetchedProjects = response.data.data.projects.map((proj) => ({
                projectName: proj.name,
                phases: proj.phases || [],
            }));
            setProjects(fetchedProjects);
        } catch (error) {
            setError("Failed to fetch projects.");
        } finally {
            setLoading(false);
        }
    };

    const getTimeSheetUser = async (selectedDate) => {
        if (!selectedDate) return;
        const { startDate, endDate } = getWeekDates(selectedDate);
        try {
            const response = await UserApi.getTimeSheetByPayload({
                userId,
                weekStart: startDate,
                weekEnd: endDate,
            });
            if (response.data) {
                const { data } = response;
                setUsername(data?.user?.name || "");
                setTimeSheetId(data.id || "");
                setStatusMessage(
                    data.status === "approved"
                        ? "Time sheet is approved."
                        : data.status === "rejected"
                            ? "Time sheet is rejected."
                            : "Your time sheet status is pending."
                );
                setStatusColor(
                    data.status === "approved"
                        ? "primary"
                        : data.status === "rejected"
                            ? "error"
                            : "green"
                );

                // Transform the data to match the expected format
                if (formikRef.current && data.rows) {
                    if (typeof data.rows === "string") {
                        data.rows = JSON.parse(data.rows);
                    }
                    const transformedRows = data.rows.map((row) => ({
                        projectName: row.projectName || "",
                        phaseName: row.phaseName || "",
                        hours: row.hours || {},
                        notes: row.notes || "",
                        description: row.description || "", // Added description field
                        isSubmited: row?.isSubmited,
                    }));

                    formikRef.current.setFieldValue("rows", transformedRows);
                    formikRef.current.setFieldValue("notes", data.notes || "");
                    formikRef.current.setFieldValue("totalHours", data.totalHours || 0);
                }
            }
        } catch (error) {
            console.error("Error fetching timesheet:", error);
            setError("Failed to fetch timesheet data");
        }
    };

    useEffect(() => {
        if (searchParams.get("date")) {
            getTimeSheetUser(searchParams.get("date"));
        } else {
          fetchProjects();
          getTimeSheetUser(selectedDate);
        }
      }, [selectedDate, searchParams, userId]);
    

    const calculateTotalHours = (rows) => {

        return rows.reduce((total, row) => {
            const rowTotal = Object.values(row.hours || {}).reduce((sum, hours) => {
                const hourValue = parseFloat(hours) || 0;
                return sum + hourValue;
            }, 0);
            return total + rowTotal;
        }, 0);
    };

    const hasRowsNeedingDescription = (rows) => {
        if (!rows || !rows.length) return false;
        return rows.some((row) => {
            const rowHours = Object.values(row?.hours || {}).reduce(
                (sum, h) => sum + (parseFloat(h) || 0),
                0
            );
            const hasProjectAndPhase = !!(row?.projectName?.trim() && row?.phaseName?.trim());
            const rowHasData = rowHours > 0 || hasProjectAndPhase;
            if (!rowHasData) return false;
            const desc = (row.description || "").trim();
            return desc.length === 0;
        });
    };

    const hasAnyRowWithEmptyDescription = (rows) => {
        if (!rows || !rows.length) return false;
        return rows.some((row) => {
            const desc = (row.description != null ? String(row.description) : "").trim();
            return desc.length === 0;
        });
    };

    const handleSubmit = async (values) => {
        if (hasRowsNeedingDescription(values.rows) || hasAnyRowWithEmptyDescription(values.rows)) {
            toast.error("Add a description of the work required for each row.");
            return;
        }
        const { startDate, endDate } = getWeekDates(selectedDate);
        const totalHours = calculateTotalHours(values.rows);

        try {
            if (timeSheetId) {
                await UserApi.updateTimeSheet({
                    id: timeSheetId,
                    userId,
                    rows: values.rows,
                    notes: values.notes,
                    weekStart: startDate,
                    weekEnd: endDate,
                    totalHours,
                });
            } else {
                const response = await UserApi.createTimeSheet({
                    userId,
                    rows: values.rows,
                    notes: values.notes,
                    weekStart: startDate,
                    weekEnd: endDate,
                    totalHours,
                });
                setTimeSheetId(response.data.timeSheetId);
            }
            setStatusMessage("Timesheet saved successfully");
        } catch (error) {
            console.error("Error saving timesheet:", error);
            setError("Failed to save timesheet");
        }
    };

    const handleApprovalStatus = async (status) => {
        try {
            await UserApi.approveTimeSheet(timeSheetId, status);
            setStatusMessage(
                status === "approved"
                    ? "Time sheet is approved."
                    : "Time sheet is rejected."
            );
            setStatusColor(status === "approved" ? "primary" : "error");
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };

    const handleExportPDF = async () => {
        const input = document.getElementById("timesheet");
        const canvas = await html2canvas(input);
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = 210;
        const padding = 10;
        const imgWidth = pageWidth - 2 * padding;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, "PNG", padding, 6, imgWidth, imgHeight);
        pdf.save("timesheet.pdf");
    };

    console.log({ selectedDate });
    return (
        <Paper elevation={3} sx={{ p: { xs: 1.5, md: 2.5 }, m: { xs: 0, md: "20px auto" }, overflowX: "hidden" }}>
            <div id="timesheet">
                <Typography variant="h4" align="center" gutterBottom>
                    Weekly Time Tracking Admin
                </Typography>

                <Box
                    sx={{
                        mt: 2,
                        mb: 1,
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: 1,
                        borderLeft: "4px solid",
                        borderColor: "primary.main",
                    }}
                >
                    <Typography variant="subtitle2" sx={{ color: "grey.800", fontWeight: 600 }} gutterBottom>
                        Description of the work required
                    </Typography>
                    <Typography variant="body2" sx={{ color: "grey.700" }}>
                        Select a project and phase for each row, enter the hours worked per day, and provide a brief description of the work done for that row. You can add multiple rows for different projects or phases. Submit when complete or use Approved/Rejected to change the timesheet status.
                    </Typography>
                </Box>

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, setFieldValue }) => (
                        <>
                            <Form>
                                <Grid container rowSpacing={2} columnSpacing={{ xs: 0, md: 2 }}>
                                    <Grid item xs={12} md={6}>
                                        <Typography>
                                            <strong>Username:</strong> {username}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ position: "relative" }}>
                                            <WeekPicker
                                                selectedDate={selectedDate}
                                                onDateChange={(date) => {
                                                    setSelectedDate(date);
                                                    formikRef.current.resetForm(); // Clear form on date change
                                                }}
                                            /></Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FieldArray name="rows">
                                            {({ push, remove }) => (
                                                <>
                                                    {/* Phones get one card per row; the table takes over from md. */}
                                                    <Box sx={{ display: { xs: "block", md: "none" } }}>
                                                        {values.rows.map((row, index) => (
                                                            <TimesheetEntryCard
                                                                key={`m-${index}`}
                                                                row={row}
                                                                index={index}
                                                                weekDays={getWeekDays(selectedDate)}
                                                                projects={projects}
                                                                phases={
                                                                    projects.find((p) => p.projectName === row.projectName)
                                                                        ?.phases || []
                                                                }
                                                                hoursDisabled={!row.projectName || !row.phaseName}
                                                                onProjectChange={(value) => {
                                                                    setFieldValue(`rows.${index}.projectName`, value);
                                                                    setFieldValue(`rows.${index}.phaseName`, "");
                                                                }}
                                                                onPhaseChange={(value) =>
                                                                    setFieldValue(`rows.${index}.phaseName`, value)
                                                                }
                                                                onHourChange={(dateString, value) =>
                                                                    setFieldValue(
                                                                        `rows.${index}.hours.${dateString}`,
                                                                        parseFloat(value) || 0
                                                                    )
                                                                }
                                                                onDescriptionChange={(text) =>
                                                                    setFieldValue(`rows.${index}.description`, text)
                                                                }
                                                                onRemove={() => remove(index)}
                                                            />
                                                        ))}
                                                    </Box>

                                                    <TableContainer
                                                        sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}
                                                    >
                                                    <Table>
                                                        <TableHead sx={{ bgcolor: "#00000017" }}>
                                                            <TableRow>
                                                                <TableCell>Project</TableCell>
                                                                <TableCell>Phase</TableCell>
                                                                {getWeekDays(selectedDate).map((day) => (
                                                                    <TableCell key={day.dateString}>
                                                                        {day.day} <br />
                                                                        {day.dateString}
                                                                    </TableCell>
                                                                ))}
                                                                <TableCell>Actions</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {values.rows.map((row, index) => {
                                                                const isEven = index % 2 == 0;
                                                                return (
                                                                    <>
                                                                        <TableRow key={index} sx={{ backgroundColor: isEven ? "#F1F4F9" : "#ffffff", borderLeft: "1px solid rgba(224, 224, 224, 1)", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                                                            <TableCell sx={{ borderBottom: "none !important" }}>
                                                                                <FormControl fullWidth>
                                                                                    <Select
                                                                                        value={row.projectName || ""}
                                                                                        onChange={(e) => {
                                                                                            setFieldValue(`rows.${index}.projectName`, e.target.value);
                                                                                            setFieldValue(`rows.${index}.phaseName`, "");
                                                                                        }}
                                                                                    >
                                                                                        <MenuItem value="" disabled>
                                                                                            Select Project
                                                                                        </MenuItem>
                                                                                        {projects
                                                                                            .slice() // Create a shallow copy to avoid mutating the original array
                                                                                            .sort((a, b) => a.projectName.localeCompare(b.projectName)) // Sort alphabetically
                                                                                            .map((project) => (
                                                                                                <MenuItem key={project.projectName} value={project.projectName}>
                                                                                                    {project.projectName}
                                                                                                </MenuItem>
                                                                                            ))}
                                                                                    </Select>
                                                                                </FormControl>
                                                                            </TableCell>

                                                                            <TableCell sx={{ borderBottom: "none !important" }}>
                                                                                <FormControl fullWidth>
                                                                                    <Select
                                                                                        value={row.phaseName || ""}
                                                                                        onChange={(e) =>
                                                                                            setFieldValue(`rows.${index}.phaseName`, e.target.value)
                                                                                        }
                                                                                        disabled={!row.projectName}
                                                                                    >
                                                                                        <MenuItem value="" disabled>
                                                                                            Select Phase
                                                                                        </MenuItem>
                                                                                        {/* {projects
                                                                                    .find((p) => p.projectName === row.projectName)
                                                                                    ?.phases.map((phase) => (
                                                                                        <MenuItem key={phase.id} value={phase.name}>
                                                                                            {phase.name}
                                                                                        </MenuItem>
                                                                                    ))} */}
                                                                                        {/* Apply sorting  */}
                                                                                        {projects
                                                                                            .find((p) => p.projectName === row.projectName)
                                                                                            ?.phases.slice() // Create a copy to avoid mutating the original array
                                                                                            .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
                                                                                            .map((phase) => (
                                                                                                <MenuItem key={phase.id} value={phase.name}>
                                                                                                    {phase.name}
                                                                                                </MenuItem>
                                                                                            ))}
                                                                                    </Select>
                                                                                </FormControl>
                                                                            </TableCell>

                                                                            {getWeekDays(selectedDate).map((day) => (
                                                                                <TableCell key={day.dateString} sx={{ borderBottom: "none !important" }}>
                                                                                    <TextField
                                                                                        sx={{ minWidth: 80 }}
                                                                                        type="number"
                                                                                        value={row.hours?.[day.dateString] || ""}
                                                                                        onChange={(e) =>
                                                                                            setFieldValue(
                                                                                                `rows.${index}.hours.${day.dateString}`,
                                                                                                parseFloat(e.target.value) || 0
                                                                                            )
                                                                                        }
                                                                                        inputProps={{ min: 0, step: 0.5 }}
                                                                                        disabled={!row.projectName || !row.phaseName}
                                                                                    />
                                                                                </TableCell>
                                                                            ))}
                                                                            <TableCell sx={{ borderBottom: "none !important" }}>
                                                                                <Button
                                                                                    variant="contained"
                                                                                    color="error"
                                                                                    onClick={() => remove(index)}
                                                                                >
                                                                                    Remove
                                                                                </Button>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                        <TableRow key={`new-${index}-description`} sx={{ backgroundColor: isEven ? "#F1F4F9" : "#ffffff", borderLeft: "1px solid rgba(224, 224, 224, 1)", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                                                            <TableCell colSpan={10}>
                                                                                <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                                                                    <Typography
                                                                                        sx={{ fontSize: "15px", fontWeight: "500" }}
                                                                                    >Description of work</Typography>
                                                                                    <TextField
                                                                                        fullWidth
                                                                                        multiline
                                                                                        rows={1}
                                                                                        placeholder="Brief description of the work done for this row"
                                                                                        value={row.description}
                                                                                        onChange={(e) =>
                                                                                            setFieldValue(`rows.${index}.description`, e.target.value)
                                                                                        }
                                                                                    />
                                                                                </Box>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                    </TableContainer>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => push(createEmptyRow())}
                                                        sx={{ mt: 2 }}
                                                    >
                                                        Add Row
                                                    </Button>
                                                </>
                                            )}
                                        </FieldArray>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{ width: "100%" }}>
                                            <Typography variant="h6" color={statusColor}>
                                                {statusMessage}
                                            </Typography>

                                            <Box sx={{
                                                display: "flex",
                                                flexDirection: { xs: "column", sm: "row" },
                                                justifyContent: "space-between",
                                                gap: 1,
                                                py: 2,
                                                "& button": { width: { xs: "100%", sm: "auto" } },
                                            }}>
                                                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => handleApprovalStatus("approved")}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Approved
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="error"
                                                        onClick={() => handleApprovalStatus("rejected")}
                                                    >
                                                        Rejected
                                                    </Button>
                                                </Box>

                                                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        color="secondary"
                                                        sx={{ mr: 1 }}
                                                        disabled={hasRowsNeedingDescription(values?.rows) || hasAnyRowWithEmptyDescription(values?.rows)}
                                                        title={hasRowsNeedingDescription(values?.rows) || hasAnyRowWithEmptyDescription(values?.rows) ? "Add a description of the work required for each row." : ""}
                                                    >
                                                        Submit
                                                    </Button>
                                                    <Button variant="contained" onClick={handleExportPDF}>
                                                        Export As PDF
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Form>
                            <Typography variant="body1" align="center" mt={2}>
                                Total Hours: {calculateTotalHours(values?.rows).toFixed(2)}
                            </Typography>
                        </>
                    )}
                </Formik>


            </div>
        </Paper>
    );
};

export default UserTimeSheet;